import { ColDef, ColumnState } from 'ag-grid-community';
import { DatePipe } from '@angular/common';
import { HttpParams } from '@angular/common/http';
import { Injectable} from '@angular/core';
import { ApiResponse } from 'src/AngularJS/Models/api-response.model';
import { CVApiService } from 'src/app/reusables/services/cv-api.service';
import { ModuleManagerExportFormat } from 'src/shared/models/module-manager/module-manager-options.model';
import { ReportingSetData, ReportingSetDataExportOptions, ReportingSetDefinitionApiModel, ReportingSetFieldType } from 'src/shared/models/pipeline-config/reporting-sets.models';


@Injectable({
    providedIn: 'root'
})
export class ReportingSetService {

    public lastDefinitionUpdate = Date.now();

    constructor(
        private cvAPIService: CVApiService,
        private datePipe: DatePipe,
    ) { }

    getAgGridColumnTypes() {
        const colTypes: { [key: string]: ColDef } = {};

        colTypes["booleanColumn"] = {};
        colTypes["numberColumn"] = {
            cellClass: 'ag-numeric-cell',
            filter: 'agNumberColumnFilter',
            headerClass: 'ag-numeric-header',
            valueFormatter: (params) => {
                if (params.node.aggData != null && params.node.aggData[params.colDef.colId] !== undefined) {
                    if (params.value.value !== undefined) {
                        params.value = params.value.value;
                    }

                    if (!Number.isInteger(params.value))
                        return Number(params.value).toFixed(2).toString();
                    else
                        return Number(params.value).toString();
                }
            },
        };
        colTypes["dateColumn"] = {
            filter: 'agDateColumnFilter',
            valueGetter: params => {
                const dateString: string = (params.data && params.data[params.colDef.field]);
                if (dateString)
                    return new Date(dateString.replace('Z', ''));
            },
            valueFormatter: params => this.datePipe.transform(params.value, CVAngularDateTimeFormat), //window['CVAngularDateTimeFormat']
            allowedAggFuncs: ['first', 'last', 'min', 'max', 'count'],
            initialAggFunc: 'first',
        };

        return colTypes;
    }

    getColumnType(fieldType: ReportingSetFieldType) {
        switch (fieldType) {
            case ReportingSetFieldType.Boolean:
                return "booleanColumn";
            case ReportingSetFieldType.DateTime:
                return "dateColumn";
            case ReportingSetFieldType.Double:
            case ReportingSetFieldType.Int:
                return "numberColumn";
            case ReportingSetFieldType.String:
            case ReportingSetFieldType.Null:
                return null; // default column type
        }
    }

    upgradeColumnStates(colDefs: ColDef[], colState: ColumnState[]) {
        const columnsInData = new Set(colDefs.map(cd => cd.colId));
        const columnsNotInData = colState.filter(col => !columnsInData.has(col.colId) && col.colId !== 'ag-Grid-AutoColumn');
        if (columnsNotInData.length === 0)
            return false; // no conversion

        // convert missing states where possible
        for (const columnState of columnsNotInData) {
            if (columnsInData.has(`Date_${columnState.colId}`))
                columnState.colId = `Date_${columnState.colId}`;
            else if (columnsInData.has(`EmpHierarchy_${columnState.colId}`))
                columnState.colId = `EmpHierarchy_${columnState.colId}`;
            else if (columnsInData.has(`ContactHierarchy_${columnState.colId}`))
                columnState.colId = `ContactHierarchy_${columnState.colId}`;
        }
        return true;
    }

    async loadReportingSetDefinitions(): Promise<ReportingSetDefinitionApiModel[]> {
        let response;
        try {
            response = await this.cvAPIService.get(`/api/v2/pipeline/reporting-set-definitions`) as ApiResponse<ReportingSetDefinitionApiModel[]>;
        } catch (error) {
            console.error("Failed loading reporting set definitions");
            throw error;
        }
        this.lastDefinitionUpdate = Date.now();
        if (response.status_code !== 200)
            throw response;
        else
            return response.data;
    }

    async loadGenericReportingSetData(
        reportingSetName: string,
        dateName?: string,
        pageName?: string,
        abort?: Promise<void>,
        startDate?: Date,
        endDate?: Date,
        groupId?: number,
        groupLevel?: number,
    ) {
        let baseUri = '';
        if (pageName)
            baseUri = await this.cvAPIService.getModuleRenderEndPoint(pageName);
        let params = new HttpParams();
        if (dateName) params = params.set("dateName", dateName);
        if (pageName) params = params.set("pageName", pageName);
        if (startDate) params = params.set("startDate", startDate.toDateString());
        if (endDate) params = params.set("endDate", endDate.toDateString());
        if (groupId) params = params.set("groupId", groupId);
        if (groupLevel) params = params.set("groupLevel", groupLevel);
        const url = `/api/v2/pipeline/reporting-set/${reportingSetName}/data?${params.toString()}`;
        return await this.cvAPIService.get(url, baseUri, abort) as ApiResponse<ReportingSetData>;
    }

    async loadGenericReportingSetExport(
        reportingSetName: string,
        format: ModuleManagerExportFormat,
        dateName?: string,
        pageName?: string,
        startDate?: Date,
        endDate?: Date,
        abort?: Promise<void>,
    ) {
        let baseUri = '';
        if (pageName)
            baseUri = await this.cvAPIService.getModuleRenderEndPoint(pageName);

        let params = new HttpParams();
        params = params.set("format", format);
        if (dateName) params = params.set("dateName", dateName.toString());
        if (pageName) params = params.set("pageName", pageName);
        if (startDate) params = params.set("startDate", startDate.toString());
        if (endDate) params = params.set("endDate", endDate.toString());
        const url = `/api/v2/pipeline/reporting-set/${reportingSetName}/export?${params.toString()}`;
        return await this.cvAPIService.get(url , baseUri, abort) as ApiResponse<string>;
    }

    async loadGenericReportingSetExportWithOptions(options: ReportingSetDataExportOptions) {
        const response = await this.cvAPIService.post<string>(`/api/v2/pipeline/reporting-set/export-options`, options);
        return response.data;
    }

}


